import { formatTime as formatTimeImpl } from "./formatter";

export { parseLocalDate } from "./helpers";

// Predefined formats.
//
// Prefer the platform-defined formats, provided in LOCALIST_SETTINGS during page load,
// but fall back to the default values if not provided.
const PREDEFINED_FORMATS = {
  detailed:
    LOCALIST_SETTINGS?.date_time_formats?.detailed ||
    "%A, %B %-d, %Y%[start| %-I:%M%P]%[end_date| - %A, %B %-d, %Y|%[end| to]]%[end| %-I:%M%P]%[start| %Z]",
  compact:
    LOCALIST_SETTINGS?.date_time_formats?.compact ||
    "%a, %b %-d%[not_current_year|, %Y]%[start| %-I%[nonzero_minutes|:%M]%P]%[end_date| - %a, %b %-d%[not_current_year|, %Y]|%[end| to]]%[end| %-I%[nonzero_minutes|:%M]%P]%[start| %Z]",
  compact_time:
    LOCALIST_SETTINGS?.date_time_formats?.compact_time ||
    "%[start|%-I%[nonzero_minutes|:%M]%P %Z]",
  admin_short_date_time:
    LOCALIST_SETTINGS?.date_time_formats?.admin_short_date_time ||
    "%-m/%-d/%Y%[start| %-I:%M%P]%[end_date| - %-m/%-d/%Y]%[end| %-I:%M%P]%[start| %Z]",
};

/**
 * Formats a time or time range using the extended strftime syntax with conditional blocks.
 *
 * Format may be an extended strftime string, or one of the known format classes:
 *   - "detailed"
 *   - "compact"
 *   - "compact_time"
 *   - "admin_short_date_time"
 * @param {Date} startTime The start time (required)
 * @param {Date} endTime The end time (optional)
 * @param {string} format The format to use, supporting custom conditional blocks
 * @param {Object} options Additional options
 * @param {Date|undefined} options.current The reference time for current-relative formatting (defaults to now)
 * @param {boolean} options.dateOnly Indicates if the range is date-only (defaults to false)
 * @returns {string} The formatted string
 */
export const formatTime = (startTime, endTime, format, options = {}) => {
  if (!(startTime instanceof Date)) {
    throw new Error("startTime must be a Date object");
  }
  if (endTime && !(endTime instanceof Date)) {
    throw new Error("endTime must be a Date object");
  }

  const { current = new Date(), dateOnly = false } = options;

  const formatString = PREDEFINED_FORMATS[format] ?? format;
  return formatTimeImpl(startTime, endTime, formatString, {
    current,
    startTimeIsDateOnly: dateOnly,
    endTimeIsDateOnly: dateOnly,
  });
};
